import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import UserMenu from '../../components/UserMenu';
import OrdersPanel from './OrdersPanel';
import MenuPanel from './MenuPanel';
import AnalyticsPanel from './AnalyticsPanel';
import AchievementsPanel from './AchievementsPanel';
import LeadsPanel from './LeadsPanel';
import VenuesPanel from './VenuesPanel';
import SettingsPanel from './SettingsPanel';
import MonetizationPanel from './MonetizationPanel';
import PosConnectionPanel from './PosConnectionPanel';
import { api } from '../../utils/apiClient';
import { useRestaurants } from '../../hooks/useRestaurants';
import './AdminPanel.css';

const VALID_TABS = ['orders', 'menu', 'analytics', 'achievements', 'venues', 'leads', 'settings', 'monetization', 'pos'] as const;
type TabName = typeof VALID_TABS[number];

function getRoleLabel(role?: string): string {
  if (!role) return '';
  const labels: Record<string, string> = {
    ADMIN: 'Admin',
    SUPER_ADMIN: 'Super Admin',
    DEVELOPER: 'Developer',
  };
  return labels[role] || role.replace('_', ' ');
}

function AdminPanel() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TabName | null;
  const activeTab: TabName = rawTab && VALID_TABS.includes(rawTab) ? rawTab : 'orders';
  const setActiveTab = (tab: TabName) =>
    setSearchParams(prev => { prev.set('tab', tab); return prev; }, { replace: true });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { restaurants } = useRestaurants();
  const [lowStockCount, setLowStockCount] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);

  const isDeveloperOrSuperAdmin = user?.role === 'DEVELOPER' || user?.role === 'SUPER_ADMIN';
  const restaurantName = isDeveloperOrSuperAdmin
    ? (restaurants.find(r => r.id === selectedRestaurantId)?.name || restaurants[0]?.name || '')
    : (user?.restaurant?.name || '');

  const restaurantLogoUrl: string | null = isDeveloperOrSuperAdmin
    ? (restaurants.find(r => r.id === selectedRestaurantId)?.logoUrl || null)
    : (user?.restaurant?.logoUrl || null);

  const [logoOverride, setLogoOverride] = useState<string | null | undefined>(undefined);
  const currentLogoUrl = logoOverride !== undefined ? logoOverride : restaurantLogoUrl;

  const restaurantDeliveryMode: 'PICKUP' | 'TABLE_SERVICE' = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.deliveryMode) ?? 'PICKUP')
    : 'PICKUP';
  const [deliveryModeOverride, setDeliveryModeOverride] = useState<'PICKUP' | 'TABLE_SERVICE' | undefined>(undefined);
  const currentDeliveryMode: 'PICKUP' | 'TABLE_SERVICE' = deliveryModeOverride ?? restaurantDeliveryMode;

  // Phase 15 — Marketplace Foundation field plumbing (mirrors deliveryMode pattern above)
  const restaurantLat: number | null = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.lat) ?? null)
    : null;
  const restaurantLng: number | null = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.lng) ?? null)
    : null;
  const restaurantOpeningHours = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.openingHours) ?? {})
    : {};
  const restaurantDineInSupported: boolean = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.dineInSupported) ?? false)
    : false;
  const restaurantAcceptsPreorders: boolean = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.acceptsPreorders) ?? false)
    : false;
  const restaurantTableCount: number = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.tableCount) ?? 0)
    : 0;

  const [latOverride, setLatOverride] = useState<number | null | undefined>(undefined);
  const [lngOverride, setLngOverride] = useState<number | null | undefined>(undefined);
  const [hoursOverride, setHoursOverride] = useState<typeof restaurantOpeningHours | undefined>(undefined);
  const [dineInOverride, setDineInOverride] = useState<boolean | undefined>(undefined);
  const [preorderOverride, setPreorderOverride] = useState<boolean | undefined>(undefined);

  // Phase 16 — cuisine + timezone
  const restaurantCuisine: string | null = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.cuisine) ?? null)
    : null;
  const restaurantTimezone: string | null = isDeveloperOrSuperAdmin
    ? ((restaurants.find(r => r.id === selectedRestaurantId)?.timezone) ?? null)
    : null;
  const [cuisineOverride, setCuisineOverride] = useState<string | null | undefined>(undefined);
  const [timezoneOverride, setTimezoneOverride] = useState<string | null | undefined>(undefined);
  const [tableCountOverride, setTableCountOverride] = useState<number | undefined>(undefined);

  const currentLat = latOverride !== undefined ? latOverride : restaurantLat;
  const currentLng = lngOverride !== undefined ? lngOverride : restaurantLng;
  const currentOpeningHours = hoursOverride ?? restaurantOpeningHours;
  const currentDineInSupported = dineInOverride !== undefined ? dineInOverride : restaurantDineInSupported;
  const currentAcceptsPreorders = preorderOverride !== undefined ? preorderOverride : restaurantAcceptsPreorders;
  const currentCuisine = cuisineOverride !== undefined ? cuisineOverride : restaurantCuisine;
  const currentTimezone = timezoneOverride !== undefined ? timezoneOverride : restaurantTimezone;
  const currentTableCount = tableCountOverride !== undefined ? tableCountOverride : restaurantTableCount;

  useEffect(() => {
    if (isDeveloperOrSuperAdmin && restaurants.length > 0 && !selectedRestaurantId) {
      setSelectedRestaurantId(restaurants[0].id);
    }
  }, [isDeveloperOrSuperAdmin, restaurants, selectedRestaurantId]);

  useEffect(() => {
    setLogoOverride(undefined);
    setDeliveryModeOverride(undefined);
    setLatOverride(undefined);
    setLngOverride(undefined);
    setHoursOverride(undefined);
    setDineInOverride(undefined);
    setPreorderOverride(undefined);
    setCuisineOverride(undefined);
    setTimezoneOverride(undefined);
    setTableCountOverride(undefined);
  }, [selectedRestaurantId]);

  useEffect(() => {
    const rid = user?.restaurantId || user?.restaurant?.id || selectedRestaurantId || restaurants[0]?.id;
    if (!rid) return;
    api.get<{ count: number }>(`/api/restaurants/${rid}/low-stock-count`)
      .then(({ data }) => setLowStockCount(data.count ?? 0))
      .catch(() => setLowStockCount(0));
    const interval = setInterval(() => {
      api.get<{ count: number }>(`/api/restaurants/${rid}/low-stock-count`)
        .then(({ data }) => setLowStockCount(data.count ?? 0))
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [user?.restaurantId, user?.restaurant?.id, selectedRestaurantId, restaurants]);

  useEffect(() => {
    const rid = user?.restaurantId || user?.restaurant?.id || selectedRestaurantId || restaurants[0]?.id;
    if (!rid) return;
    api.get<{ orders?: unknown[] }>(`/api/orders/restaurant/${rid}`, { params: { limit: 200 } })
      .then(({ data }) => {
        const orders = data.orders || [];
        const active = (orders as { status?: string }[]).filter(o => o.status !== 'COMPLETED' && o.status !== 'CANCELLED');
        setActiveOrdersCount(active.length);
      })
      .catch(() => setActiveOrdersCount(0));
  }, [user?.restaurantId, user?.restaurant?.id, selectedRestaurantId, restaurants]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="admin-container">
      <div className="admin-topbar">
        <Link to="/" className="admin-logo">
          {currentLogoUrl
            ? <img src={currentLogoUrl} alt="logo" className="admin-logo-mark admin-logo-mark--img" />
            : <img src="/logo-olso.png" alt="OLSO" className="admin-logo-img" />
          }
        </Link>
        <div className="admin-topbar-divider" />
        {isDeveloperOrSuperAdmin ? (
          <select
            className="admin-restaurant-select"
            value={selectedRestaurantId}
            onChange={(e) => setSelectedRestaurantId(e.target.value)}
          >
            {restaurants.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        ) : (
          <div className="admin-restaurant-badge">{restaurantName || t('admin.title')}</div>
        )}
        <div className="admin-topbar-spacer" />
        <div className="admin-lang-switcher">
          {(['ru', 'en', 'kk'] as const).map(lang => (
            <button
              key={lang}
              className={`admin-lang-btn ${i18n.language === lang ? 'active' : ''}`}
              onClick={() => i18n.changeLanguage(lang)}
            >
              {lang === 'ru' ? 'РУ' : lang === 'en' ? 'EN' : 'ҚАЗ'}
            </button>
          ))}
        </div>
        <UserMenu
          name={user?.name || user?.email || 'Admin'}
          role={getRoleLabel(user?.role)}
          restaurantName={isDeveloperOrSuperAdmin ? undefined : restaurantName}
          onChangePassword={() => setShowChangePassword(true)}
          onLogout={handleLogout}
          theme="light"
        />
      </div>

      <div className="admin-tabs">
        <div
          className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          {t('admin.orders')}
          {activeOrdersCount > 0 && <span className="admin-tab-badge">{activeOrdersCount}</span>}
        </div>
        <div
          className={`admin-tab ${activeTab === 'menu' ? 'active' : ''}`}
          onClick={() => setActiveTab('menu')}
        >
          {t('admin.menu')}
          {lowStockCount > 0 && (
            <span className="admin-tab-badge" title={t('menu.lowStockBadge')}>{lowStockCount}</span>
          )}
        </div>
        <div
          className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          {t('admin.analytics')}
        </div>
        <div
          className={`admin-tab ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}
        >
          {t('admin.achievements')}
        </div>
        {isDeveloperOrSuperAdmin && (
          <>
            <div
              className={`admin-tab ${activeTab === 'venues' ? 'active' : ''}`}
              onClick={() => setActiveTab('venues')}
            >
              {t('admin.venues')}
            </div>
            <div
              className={`admin-tab ${activeTab === 'leads' ? 'active' : ''}`}
              onClick={() => setActiveTab('leads')}
            >
              {t('admin.leads')}
            </div>
          </>
        )}
        <div
          className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          {t('admin.settings', 'Настройки')}
        </div>
        <div
          className={`admin-tab ${activeTab === 'monetization' ? 'active' : ''}`}
          onClick={() => setActiveTab('monetization')}
        >
          {t('admin.monetization', 'Монетизация')}
        </div>
        <div
          className={`admin-tab ${activeTab === 'pos' ? 'active' : ''}`}
          onClick={() => setActiveTab('pos')}
        >
          {t('admin.pos', 'Касса (POS)')}
        </div>
      </div>

      <div className="admin-content">
        {activeTab === 'orders' && (
          <OrdersPanel
            restaurantId={isDeveloperOrSuperAdmin ? selectedRestaurantId : (user?.restaurantId || user?.restaurant?.id || '')}
          />
        )}
        {activeTab === 'menu' && <MenuPanel />}
        {activeTab === 'analytics' && <AnalyticsPanel />}
        {activeTab === 'achievements' && <AchievementsPanel />}
        {activeTab === 'venues' && isDeveloperOrSuperAdmin && <VenuesPanel />}
        {activeTab === 'leads' && isDeveloperOrSuperAdmin && <LeadsPanel />}
        {activeTab === 'monetization' && (
          <MonetizationPanel
            restaurantId={isDeveloperOrSuperAdmin && selectedRestaurantId
              ? selectedRestaurantId
              : (user?.restaurantId || user?.restaurant?.id || '')}
          />
        )}
        {activeTab === 'pos' && (
          <PosConnectionPanel
            restaurantId={isDeveloperOrSuperAdmin && selectedRestaurantId
              ? selectedRestaurantId
              : (user?.restaurantId || user?.restaurant?.id || '')}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsPanel
            restaurantId={isDeveloperOrSuperAdmin && selectedRestaurantId
              ? selectedRestaurantId
              : (user?.restaurantId || user?.restaurant?.id || '')}
            currentLogoUrl={currentLogoUrl}
            currentDeliveryMode={currentDeliveryMode}
            currentLat={currentLat}
            currentLng={currentLng}
            currentOpeningHours={currentOpeningHours}
            currentDineInSupported={currentDineInSupported}
            currentAcceptsPreorders={currentAcceptsPreorders}
            currentCuisine={currentCuisine}
            currentTimezone={currentTimezone}
            onLogoUpdated={(url) => setLogoOverride(url)}
            onDeliveryModeUpdated={(mode) => setDeliveryModeOverride(mode)}
            onLatLngUpdated={(lat, lng) => { setLatOverride(lat); setLngOverride(lng); }}
            onOpeningHoursUpdated={(hours) => setHoursOverride(hours)}
            onDineInUpdated={(value) => setDineInOverride(value)}
            onPreorderUpdated={(value) => setPreorderOverride(value)}
            onCuisineUpdated={(value) => setCuisineOverride(value)}
            onTimezoneUpdated={(value) => setTimezoneOverride(value)}
            currentTableCount={currentTableCount}
            onTableCountUpdated={(val) => setTableCountOverride(val)}
          />
        )}
      </div>

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
}

export default AdminPanel;

